import { oneOfType, object, array, bool, string } from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { cardThemes } from '../../ProductCard/ProductCard.config';
import { getPriceFormatWithCommas } from '../../../helpers/Pricing/Pricing';
import * as styles from '../Pricing.css';
import BogoTag from '../../../assets/svg/BogoTag.svg';

const cx = classNames.bind(styles);
const priceDisplayPattern = /^([\d,]+)\.?(\d{0,2})/;
const newPriceFormat = (priceValue) => {
    if (!priceValue) {
        return priceValue;
    }
    const results = [];
    const data = cardThemes.CARTRIDGE ? `${getPriceFormatWithCommas(priceValue)}` : `${priceValue}`;
    const value = priceDisplayPattern.exec(data.trim());
    if (!value) {
        results.push(data);
        return results;
    }
    const [dollarAndCent] = value;

    results.push(`$${dollarAndCent}`);

    return results;
};

const stripZerosPattern = /(\.00$|\.$)/;

const Pricing = ({
    pricingDetails,
    customMarketingLabel,
    cardTheme,
    deviceType: { isMobile = false },
    displayEach,
    dynamicFont,
    eligibilityMessage,
    mainPriceClass,
    mapPriceClass,
    ppId,
    priceClass,
    priceLabelClass,
    priceWrapperClass,
    pricingConfig,
    priceSummaryClass,
}) => {
    const {
        promotions,
        marketingLabel,
        brand,
        defaults,
        defaultsPriceValue,
        eachLabel,
        fpac,
        fpacCoupon,
        fpacPrice,
        isDefaultsPriceRange,
        isOriginalPriceRange,
        mapPrice,
        original,
        originalPriceValue,
        showNoPriceLabel,
        subDivision,
        originalType,
        percentOff,
        dashed,
        type,
        currentMin,
        currentMax,
        originalPriceLabel,
        currentPriceLabel,
    } = pricingDetails;

    const {
        from,
        afterCoupon,
        disableFPACAPI,
        disableFPAC,
        disableFromLableInRange,
        enablePriceSuperScript = false,
    } = pricingConfig;

    const defaultPriceLabel = {
        noPriceLabel: 'View Price in Cart',
        mapPrice: 'View Price in Cart',
    };

    const {
        preferences: { priceLabel = defaultPriceLabel },
        featureFlags: {
            disableEachLabel = false,
            enableCustomAfterCouponLabel = false,
            enableMarketingLabel = false,
        },
    } = useSelector(({ context }) => context);

    const priceText = useRef();

    const getNewCouponPriceDetails = (showNewPriceFormat, displayPrice, rangePrice) => {
        if (!showNewPriceFormat) {
            return {};
        }
        const priceFormat = newPriceFormat(displayPrice);
        const price = isNaN(displayPrice)
            ? ''
            : String(displayPrice).replace(stripZerosPattern, '');
        const isDecimalExist = price.indexOf('.') !== -1;
        const priceLength = price.length;
        let isWrapText;
        if (rangePrice) {
            isWrapText = priceLength <= 2;
        } else {
            isWrapText = isDecimalExist ? priceLength <= 7 : priceLength <= 5;
            // In Mobile channel, due to space constrainst going to second with wrap. So fix
            // is made to move to next line without wrap
            if (isMobile) {
                isWrapText = isDecimalExist ? priceLength <= 6 : priceLength <= 5;
            }
        }
        const isFullText = !isWrapText;
        return {
            newPrice: priceFormat,
            isWrapText,
            isFullText,
        };
    };

    const getFPACDisplayDetail = () => {
        const showNewPriceFormat = disableFPACAPI === false;
        const showCouponInfoPrice = showNewPriceFormat && disableFPAC === false;
        const fromLabel = from;
        const afterCouponLabel =
            enableCustomAfterCouponLabel && eligibilityMessage ? eligibilityMessage : afterCoupon;
        return {
            showNewPriceFormat,
            showCouponInfoPrice,
            ...(showNewPriceFormat ? { fromLabel, afterCouponLabel } : undefined),
            ...(enableCustomAfterCouponLabel ? { afterCouponLabel } : undefined),
        };
    };

    const getFPACRenderDetail = () => {
        const { fpac: fpacData, nonfpac = {} } = pricingConfig;
        let { disableWasPrice = false, disablePercentageOff = false } = nonfpac;

        if (type === 'fpac' || type === 'fpac-range') {
            const {
                disableWasPrice: disableWPrice,
                disablePercentageOff: disablePerOff,
            } = fpacData;
            disableWasPrice = disableWPrice || false;
            disablePercentageOff = disablePerOff || false;
        }
        return {
            showWasSummary: !disableWasPrice,
            showPercentOff: !disablePercentageOff,
        };
    };

    const getPriceRangeInSuperScript = (displayPrice) => {
        const numberMatchingPattern = /[^0-9.]+/g;
        const priceArray = `${displayPrice}`.split('-');
        return priceArray.map((price, index) => {
            let priceVal = Number(price.replace(numberMatchingPattern, ''));
            priceVal = priceVal % 1 !== 0 ? priceVal.toFixed(2) : priceVal;
            const priceInSubScriptFormat = newPriceFormat(priceVal);

            if (index !== priceArray.length - 1) {
                priceInSubScriptFormat.push(' - ');
            }
            return priceInSubScriptFormat;
        });
    };

    const getNewRangePrice = (displayPrice, isPriceRange) => {
        if (isPriceRange && enablePriceSuperScript) {
            return getPriceRangeInSuperScript(displayPrice);
        }
        return newPriceFormat(displayPrice);
    };

    const isSaleSummary = useCallback(() => {
        return originalType || original || dashed || percentOff;
    }, [originalType, original, dashed, percentOff]);

    const renderWasPriceStrike = (showSaleSummary, showWasSummary, showPercentOff) => {
        return (
            showSaleSummary &&
            isSaleSummary() && (
                <div
                    data-automation-id="price-old-sale"
                    className={cx('salesummary', priceSummaryClass)}
                >
                    {showWasSummary && original && <strike>{showWasSummary && original}</strike>}
                    {showWasSummary && showPercentOff ? dashed : ''}
                    {showPercentOff && percentOff && (
                        <span
                            data-automation-id="price-percent-off"
                            className={cx('percentageOff')}
                        >
                            {percentOff}
                        </span>
                    )}
                </div>
            )
        );
    };

    const getPriceDetails = () => {
        return (
            <>
                {currentMin ? (
                    <input type="hidden" value={currentMin} className="currentMin" />
                ) : null}
                {currentMax ? (
                    <input type="hidden" value={currentMax} className="currentMax" />
                ) : null}
                {originalPriceLabel ? (
                    <input
                        type="hidden"
                        value={originalPriceLabel}
                        className="originalPriceLabel"
                    />
                ) : null}
                {currentPriceLabel ? (
                    <input type="hidden" value={currentPriceLabel} className="currentPriceLabel" />
                ) : null}
            </>
        );
    };

    const getDisplayPrice = useMemo(() => {
        const {
            afterCouponLabel,
            fromLabel,
            showCouponInfoPrice,
            showNewPriceFormat,
        } = getFPACDisplayDetail();

        const localPrice = defaults || original;
        const isPriceRange = isDefaultsPriceRange || isOriginalPriceRange;
        const displayPrice = isPriceRange ? localPrice : defaultsPriceValue || originalPriceValue;

        const newLocalPrice = enablePriceSuperScript && (
            <span className={cx('priceInBlock')}>
                {getNewRangePrice(displayPrice, isPriceRange)}
            </span>
        );

        const priceCustomClass = cx(
            'priceWrapper',
            { supPriceFormat: showNewPriceFormat },
            priceWrapperClass
        );

        const { showWasSummary, showPercentOff } = getFPACRenderDetail();
        const showSaleSummary = showWasSummary || showPercentOff;
        const isFpacRange = type === 'fpac-range';

        if (type === 'fpac' || isFpacRange) {
            const fpacPriceRange = disableFromLableInRange ? fpac : fpacPrice;

            const { isFullText, isWrapText, newPrice } = getNewCouponPriceDetails(
                showCouponInfoPrice,
                fpacPriceRange,
                isFpacRange
            );

            const fpacNewLocalPrice = enablePriceSuperScript ? (
                <span className={cx('priceInBlock')}>
                    {getNewRangePrice(newPrice, enablePriceSuperScript)}
                </span>
            ) : (
                newPrice
            );
            const priceLimit = 7; // for mobile devices to check for length of the price
            const fpacCouponTextMobile =
                (fpacNewLocalPrice?.length || fpac?.length) > priceLimit && 'fpacCouponTextLength';

            return (
                <div className={cx('fpacBlock', { newFPACWrapper: showCouponInfoPrice })}>
                    {isFpacRange && !disableFromLableInRange && (
                        <span className={cx('fpacText')}>{fromLabel || 'from'}</span>
                    )}
                    <span
                        className={cx('fpacPrice', {
                            fpacNoRange: !isFpacRange,
                            noSpaceText:
                                isFullText || (enableCustomAfterCouponLabel && eligibilityMessage),
                            wrap: isWrapText,
                        })}
                    >
                        {fpacNewLocalPrice || fpac}
                    </span>
                    <span
                        className={cx('fpacText', fpacCouponTextMobile, {
                            inlinefpacText: !dynamicFont,
                            fpacRange: isFpacRange,
                            newFPACCouponFullText:
                                isFullText || (enableCustomAfterCouponLabel && eligibilityMessage),
                            newFPACCouponText: isWrapText,
                            customAfterCouponLabel:
                                enableCustomAfterCouponLabel && eligibilityMessage,
                        })}
                    >
                        {afterCouponLabel || 'with code'}
                    </span>
                    {renderWasPriceStrike(showSaleSummary, showWasSummary, showPercentOff)}
                    <input type="hidden" value={fpacCoupon} className="fpacCoupon" />
                    {ppId && <input type="hidden" value={ppId} className="ppId" />}
                    {brand && <input type="hidden" value={brand} className="brand" />}
                    {subDivision && (
                        <input className="subDivision" type="hidden" value={subDivision} />
                    )}
                    {getPriceDetails()}
                </div>
            );
        }

        const mainPriceClazz = showNewPriceFormat
            ? cx('price', priceClass, mainPriceClass)
            : cx('price', priceClass);

        let pricingLabel = '';

        if (showNoPriceLabel) {
            pricingLabel = priceLabel.noPriceLabel;
        } else if (mapPrice) {
            pricingLabel = priceLabel.mapPrice;
        }

        const showEach = !disableEachLabel && eachLabel && eachLabel === 'each';

        return (
            <>
                {pricingLabel ? (
                    <p
                        className={cx('mapPrice', mapPriceClass)}
                        data-automation-id="at-price-map-price-label"
                    >
                        <span className={cx('pricingLabel')}>{pricingLabel}</span>
                        {getPriceDetails()}
                        <div
                            className={dt([
                                'font-open-sans',
                                'font-normal',
                                'italic',
                                'text-sm',
                                'leading-5',
                                'tracking-normal',
                                'line-through',
                                'text-gray-60',
                                'inline',
                                'w-full',
                            ])}
                        >
                            {original}
                        </div>
                    </p>
                ) : (
                    <p
                        data-automation-id={`${type}-price-info`}
                        ref={priceText}
                        className={priceCustomClass}
                    >
                        {getPriceDetails()}
                        {showNewPriceFormat && isPriceRange && !disableFromLableInRange && (
                            <span className={cx('newPriceText')}>{fromLabel || 'from'}</span>
                        )}
                        <span data-automation-id="at-price-value" className={mainPriceClazz}>
                            {newLocalPrice || localPrice}
                            {showEach ? (
                                <span className={cx('eachLabelBundleInfo')}>each</span>
                            ) : null}
                        </span>
                        {displayEach && <span className={cx('eachLabel')}>each</span>}
                        {type && (
                            <span
                                data-automation-id="at-price-label"
                                className={cx(priceLabelClass, {
                                    sale: true,
                                    clearence: type === 'clearance' || false,
                                })}
                            >
                                {type}
                            </span>
                        )}
                        {renderWasPriceStrike(showSaleSummary, showWasSummary, showPercentOff)}
                    </p>
                )}
            </>
        );
    }, [
        defaults,
        original,
        isDefaultsPriceRange,
        isOriginalPriceRange,
        isOriginalPriceRange,
        originalPriceValue,
        enablePriceSuperScript,
        type,
        disableFromLableInRange,
        fpac,
        fpacPrice,
        enableCustomAfterCouponLabel,
        eligibilityMessage,
        dynamicFont,
        fpacCoupon,
        ppId,
        brand,
        subDivision,
        priceClass,
        mainPriceClass,
        showNoPriceLabel,
        mapPrice,
        disableEachLabel,
        eachLabel,
        displayEach,
    ]);

    if (!pricingDetails) {
        return null;
    }

    const isGallaryTheme = cardTheme === cardThemes.GALLERY;

    return (
        <div className={cx('pricing', 'displayPriceWrapper', { gallery: isGallaryTheme })}>
            {(promotions || marketingLabel) && (
                <div className={cx('customerMarketingLabelWrap')}>
                    {promotions ? (
                        <p
                            className={cx('marketingLabel', customMarketingLabel)}
                            data-automation-id="at-price-promotions-label"
                        >
                            {promotions.toString().match('BUY 1 GET 1') ? (
                                <Icon iconType="svg" width="24px" height="24px" viewBox="0 0 24 24">
                                    <BogoTag />
                                </Icon>
                            ) : null}
                            {promotions}
                        </p>
                    ) : null}
                    {marketingLabel && enableMarketingLabel ? (
                        <p
                            className={cx('marketingLabel', customMarketingLabel)}
                            data-automation-id="at-price-marketing-label"
                        >
                            {marketingLabel}
                        </p>
                    ) : null}
                </div>
            )}
            {getDisplayPrice}
        </div>
    );
};
Pricing.defaultProps = {
    pricingDetails: {},
    dynamicFont: false,
    priceWrapperClass: '',
    priceClass: '',
    mainPriceClass: '',
    priceLabelClass: '',
    priceSummaryClass: '',
    customMarketingLabel: '',
    mapPriceClass: '',
    pricingConfig: {},
    deviceType: {},
    ppId: '',
    displayEach: false,
    cardTheme: '',
    eligibilityMessage: '',
};

Pricing.propTypes = {
    pricingDetails: oneOfType([object, array]),
    dynamicFont: bool,
    priceWrapperClass: string,
    priceClass: string,
    mainPriceClass: string,
    priceLabelClass: string,
    priceSummaryClass: string,
    customMarketingLabel: string,
    mapPriceClass: string,
    pricingConfig: object,
    deviceType: object,
    ppId: string,
    displayEach: bool,
    cardTheme: string,
    eligibilityMessage: string,
};

export default Pricing;
